import { apiErrorText } from "../../../api/api-error.js";
import { portalFormat, portalLocale, portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `versions` — what the selected version answers from (plan-018 WAPP-B, design §30.12,
 * ADR 0059). No new screen: a card under the version history, drawn only for a version that has an information index
 * (`getAgentVersion`'s `information` is not `null` — today the "Customer support" agent_template's). Nothing here branches
 * on a template's code.
 *
 * THE OWNER SEES WHAT EACH VERSION KNEW. The card names the version and reads, for it:
 *
 *   · the pasted text and the documents (from the version's answers — names and sizes; the portal has no route that
 *     hands back a document's content, so the text itself is not shown here);
 *   · the website address, and the reading the version was built from: when it ran, how it ended, the pages read (each
 *     opens its text, as the build kept it, from the reading's snapshot) and the pages skipped, each with its reason;
 *   · how many passages the version's index holds.
 *
 * "READ THE WEBSITE AGAIN" (owner answer Q3) is offered on the live version only — it reads the live version's website
 * address again and builds a DRAFT from the fresh reading (ADR 0043), which answers nobody until it is published. The
 * live version keeps answering meanwhile. The build counts towards the organization's hourly build limit, and the API's
 * refusals (a build already running, no website address, the limit) are shown in its words. On success the Versions
 * tab says so ({@link RereadNotice}) and selects the new draft — whose own card appears once it is built, because a
 * version's information exists only from its build.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Icon, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useMemberCan } from "../../../shell/member-capability.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import type { AgentVersionSummaryView, ConsoleGateway } from "../../../api/gateway.js";
import type {
  AgentVersionInformationView,
  WebsiteReadingView,
  WebsiteRereadView,
} from "../../../api/slice/website-reading.js";

import "./information-panel.css";

/** How often a reading still running is read again. */
const READING_POLL_MS = 4_000;

/** Under the version list's own key, so a publish, an edit or a re-read refreshes these too. */
export const agentVersionInformationQuery = (gateway: ConsoleGateway, voiceAgentId: string, agentVersionId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId, agentVersionId, "information"],
    queryFn: (): Promise<AgentVersionInformationView | null> =>
      gateway.readAgentVersionInformation(voiceAgentId, agentVersionId),
  });

const websiteReadingQuery = (gateway: ConsoleGateway, voiceAgentId: string, agentVersionId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId, agentVersionId, "website-reading"],
    queryFn: (): Promise<WebsiteReadingView | null> => gateway.readWebsiteReading(voiceAgentId, agentVersionId),
  });

const agentVersionDocumentQuery = (gateway: ConsoleGateway, voiceAgentId: string, agentVersionId: string) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId, agentVersionId, "answers"],
    queryFn: async () => (await gateway.getVoiceAgentAnswers(voiceAgentId, agentVersionId)).document,
  });

const websitePageQuery = (gateway: ConsoleGateway, voiceAgentId: string, agentVersionId: string, ordinal: number) =>
  queryOptions({
    queryKey: [...QUERY_KEY.agentVersion, voiceAgentId, agentVersionId, "website-page", ordinal],
    queryFn: () => gateway.readWebsitePage(voiceAgentId, ordinal, agentVersionId),
  });

/** A file's size in the person's language: `2.1 KB`, `96 KB`, `1.4 MB`. */
export function byteSizeText(byteSize: number): string {
  const format = portalFormat(portalLocale());
  if (byteSize < 1_000_000) {
    const kilobyte = byteSize / 1_000;
    return portalText("operations", "information.size_kb", {
      v0: format.number(kilobyte, { maximumFractionDigits: kilobyte < 10 ? 1 : 0 }),
    });
  }
  return portalText("operations", "information.size_mb", {
    v0: format.number(byteSize / 1_000_000, { maximumFractionDigits: 1 }),
  });
}

/** Why a reading failed, from the API's closed list (design §30.4.1); an unknown code is shown as it is. */
export function readingFailureTextOf(code: string): string {
  switch (code) {
    case "address_refused":
      return portalText("operations", "information.failure_address_refused");
    case "unresolved":
      return portalText("operations", "information.failure_unresolved");
    case "blocked_address":
      return portalText("operations", "information.failure_blocked_address");
    case "unreachable":
      return portalText("operations", "information.failure_unreachable");
    case "tls":
      return portalText("operations", "information.failure_tls");
    case "robots_disallowed":
      return portalText("operations", "information.failure_robots_disallowed");
    case "robots_unavailable":
      return portalText("operations", "information.failure_robots_unavailable");
    case "timeout":
      return portalText("operations", "information.failure_timeout");
    case "not_html":
      return portalText("operations", "information.failure_not_html");
    case "script_only":
      return portalText("operations", "information.failure_script_only");
    case "no_text":
      return portalText("operations", "information.failure_no_text");
    case "platform":
      return portalText("operations", "information.failure_platform");
    default:
      return code;
  }
}

/** Why a page was left out, from the API's closed list (design §30.4.1, D7); an unknown reason is shown as it is. */
export function skipReasonTextOf(reason: string): string {
  switch (reason) {
    case "robots":
      return portalText("operations", "information.skip_robots");
    case "noindex":
      return portalText("operations", "information.skip_noindex");
    case "content_type":
      return portalText("operations", "information.skip_content_type");
    case "page_limit":
      return portalText("operations", "information.skip_page_limit");
    case "depth_limit":
      return portalText("operations", "information.skip_depth_limit");
    case "byte_limit":
      return portalText("operations", "information.skip_byte_limit");
    case "timeout":
      return portalText("operations", "information.skip_timeout");
    case "blocked_address":
      return portalText("operations", "information.skip_blocked_address");
    case "off_site":
      return portalText("operations", "information.skip_off_site");
    case "duplicate":
      return portalText("operations", "information.skip_duplicate");
    case "redirect_limit":
      return portalText("operations", "information.skip_redirect_limit");
    case "no_text":
      return portalText("operations", "information.skip_no_text");
    case "undecodable":
      return portalText("operations", "information.skip_undecodable");
    case "error":
      return portalText("operations", "information.skip_error");
    default:
      return reason;
  }
}

/** The reading's state as a tag. */
function ReadingOutcomeTag({ reading }: { readonly reading: WebsiteReadingView }): ReactNode {
  usePortalTranslation("operations");
  if (reading.outcome === null) return <Tag status="caution">{portalText("operations", "information.outcome_running")}</Tag>;
  if (reading.outcome === "read") return <Tag status="positive">{portalText("operations", "information.outcome_read")}</Tag>;
  if (reading.outcome === "partial") return <Tag status="caution">{portalText("operations", "information.outcome_partial")}</Tag>;
  return <Tag status="critical">{portalText("operations", "information.outcome_failed")}</Tag>;
}

/** "Read 23 pages on 13 Oct 2026, 14:02; 4 skipped." */
export function readingSummaryText(reading: WebsiteReadingView): string {
  if (reading.outcome === null) return portalText("operations", "information.reading_running");
  const at = portalFormat(portalLocale()).date(reading.finishedAt ?? reading.startedAt, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const read = portalText("operations", "information.reading_read", {
    count: reading.pageReadCount,
    v0: String(reading.pageReadCount),
    v1: at,
  });
  if (reading.pageSkippedCount === 0) return read;
  return portalText("operations", "information.reading_skipped", { v0: read, v1: String(reading.pageSkippedCount) });
}

export interface InformationPanelProps {
  readonly voiceAgentId: string;
  readonly selected: AgentVersionSummaryView;
  /** The live version's label, for "v4 keeps answering", or `null` with no live version. */
  readonly liveLabel: string | null;
  /** The draft "Read the website again" opened: the tab says so and selects it. */
  readonly onReread: (made: WebsiteRereadView) => void;
}

export function InformationPanel(props: InformationPanelProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const information = useQuery(agentVersionInformationQuery(gateway, props.voiceAgentId, props.selected.agentVersionId));
  // Only a version with an information index draws the card; nothing is asked for any other.
  if (information.data === null) return null;
  if (information.data === undefined) {
    return information.isError ? (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "information.could_not_read")} {apiErrorText(information.error, "")}
      </p>
    ) : null;
  }
  return <InformationCard {...props} information={information.data} />;
}

function InformationCard({
  voiceAgentId,
  selected,
  liveLabel,
  onReread,
  information,
}: InformationPanelProps & { readonly information: AgentVersionInformationView }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const document = useQuery(agentVersionDocumentQuery(gateway, voiceAgentId, selected.agentVersionId));
  const reading = useQuery({
    ...websiteReadingQuery(gateway, voiceAgentId, selected.agentVersionId),
    enabled: information.websiteAddress !== null || information.websiteReadingId !== null,
    refetchInterval: (query) => (query.state.data?.outcome === null ? READING_POLL_MS : false),
  });
  const writeRefusal = useMemberCan().whyNot("voice_agent.write");
  const [rereadError, setRereadError] = useState<unknown>(null);

  const readAgain = useMutation({
    mutationFn: () => gateway.readWebsiteAgain(voiceAgentId),
    async onSuccess(made) {
      setRereadError(null);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      onReread(made);
    },
    onError(error) {
      setRereadError(error);
    },
  });

  const pastedText = document.data?.filter((one) => one.agentDocumentId === information.pastedTextDocumentId) ?? [];
  const attached = document.data?.filter((one) => one.agentDocumentId !== information.pastedTextDocumentId) ?? [];
  const offerReread = selected.isLive && information.websiteAddress !== null;

  return (
    <section className="c-va-card c-info" aria-labelledby="c-info-title">
      <div className="c-info-head">
        <div className="c-info-head-copy">
          <h3 className="c-va-card-title" id="c-info-title">
            {portalText("operations", "information.title", { v0: selected.label })}
          </h3>
          <p className="c-va-card-dek">
            {portalText("operations", "information.dek", {
              count: information.passageCount,
              v0: portalFormat(portalLocale()).number(information.passageCount),
            })}
          </p>
        </div>
        {offerReread ? (
          <div className="c-va-actions">
            <span title={writeRefusal ?? undefined}>
              <Button
                variant="secondary"
                disabled={writeRefusal !== null || readAgain.isPending}
                onClick={() => {
                  readAgain.mutate();
                }}
              >
                <Icon glyph={GLYPH.arrowCounterClockwise} size="md" />
                <span>
                  {readAgain.isPending
                    ? portalText("operations", "working_13b7bfca")
                    : portalText("operations", "information.read_again")}
                </span>
              </Button>
            </span>
          </div>
        ) : null}
      </div>

      {offerReread ? (
        <p className="c-info-note">
          {portalText("operations", "information.read_again_note", { v0: liveLabel ?? selected.label })}
        </p>
      ) : null}
      {rereadError === null ? null : (
        <p className="c-va-alert c-va-alert-critical c-va-alert-flush" role="alert">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>{apiErrorText(rereadError, portalText("operations", "information.read_again_failed"))}</span>
        </p>
      )}

      <dl className="c-info-inputs">
        <div>
          <dt>{portalText("operations", "information.label_pasted_text")}</dt>
          <dd>
            {document.data === undefined ? (
              document.isError ? portalText("operations", "information.documents_unreadable") : "…"
            ) : pastedText.length === 0 ? (
              <span className="c-info-none">{portalText("operations", "information.none")}</span>
            ) : (
              pastedText.map((one) => (
                <span key={one.agentDocumentId} className="c-info-file">
                  <Icon glyph={GLYPH.clipboardText} size="md" />
                  <span>{portalText("operations", "information.pasted_text_size", { v0: byteSizeText(one.byteSize) })}</span>
                </span>
              ))
            )}
          </dd>
        </div>
        <div>
          <dt>{portalText("operations", "information.label_documents")}</dt>
          <dd>
            {document.data === undefined ? (
              document.isError ? portalText("operations", "information.documents_unreadable") : "…"
            ) : attached.length === 0 ? (
              <span className="c-info-none">{portalText("operations", "information.none")}</span>
            ) : (
              attached.map((one) => (
                <span key={one.agentDocumentId} className="c-info-file">
                  <Icon glyph={GLYPH.fileText} size="md" />
                  <span className="c-info-file-name">{one.filename}</span>
                  <span className="c-info-file-size">{byteSizeText(one.byteSize)}</span>
                </span>
              ))
            )}
          </dd>
        </div>
        <div>
          <dt>{portalText("operations", "information.label_website")}</dt>
          <dd>
            {information.websiteAddress === null ? (
              <span className="c-info-none">{portalText("operations", "information.none")}</span>
            ) : (
              <span className="c-info-file">
                <Icon glyph={GLYPH.globe} size="md" />
                <span className="c-info-file-name">{information.websiteAddress}</span>
              </span>
            )}
          </dd>
        </div>
      </dl>

      {information.websiteAddress === null && information.websiteReadingId === null ? null : reading.data === undefined ? (
        reading.isError ? (
          <p className="c-note c-note-critical" role="alert">
            {portalText("operations", "information.reading_unreadable")} {apiErrorText(reading.error, "")}
          </p>
        ) : (
          <p className="c-note">{portalText("operations", "information.reading_loading")}</p>
        )
      ) : reading.data === null ? (
        <p className="c-note">{portalText("operations", "information.reading_none")}</p>
      ) : (
        <WebsiteReading voiceAgentId={voiceAgentId} agentVersionId={selected.agentVersionId} reading={reading.data} />
      )}
    </section>
  );
}

function WebsiteReading({
  voiceAgentId,
  agentVersionId,
  reading,
}: {
  readonly voiceAgentId: string;
  readonly agentVersionId: string;
  readonly reading: WebsiteReadingView;
}): ReactNode {
  usePortalTranslation("operations");
  const [opened, setOpened] = useState<number | null>(null);
  return (
    <div className="c-info-reading">
      <div className="c-info-reading-head">
        <p className="c-va-version-what">{portalText("operations", "information.reading_title")}</p>
        <ReadingOutcomeTag reading={reading} />
      </div>
      <p className="c-info-note">{readingSummaryText(reading)}</p>
      {reading.outcome === "failed" && reading.failureCode !== null ? (
        <p className="c-va-alert c-va-alert-critical c-va-alert-flush" role="note">
          <span className="c-va-alert-icon">
            <Icon glyph={GLYPH.warningCircle} size="lg" />
          </span>
          <span>{readingFailureTextOf(reading.failureCode)}</span>
        </p>
      ) : null}
      {reading.strippedCharacterCount > 0 ? (
        <p className="c-info-note">
          {portalText("operations", "information.stripped", { v0: String(reading.strippedCharacterCount) })}
        </p>
      ) : null}

      {reading.readPage.length === 0 ? null : (
        <>
          <p className="c-va-caps">
            {portalText("operations", "information.pages_read", { v0: String(reading.readPage.length) })}
          </p>
          <ul className="c-info-pages">
            {reading.readPage.map((page) => (
              <li key={page.ordinal} className="c-info-page" data-open={opened === page.ordinal ? "true" : undefined}>
                <button
                  type="button"
                  className="c-info-page-row"
                  aria-expanded={opened === page.ordinal}
                  onClick={() => {
                    setOpened(opened === page.ordinal ? null : page.ordinal);
                  }}
                >
                  <span className="c-info-page-caret">
                    <Icon glyph={opened === page.ordinal ? GLYPH.caretDown : GLYPH.caretRight} size="md" />
                  </span>
                  <span className="c-info-page-copy">
                    <span className="c-info-page-title">{page.title ?? page.finalUrl}</span>
                    <span className="c-info-page-url">{page.finalUrl}</span>
                  </span>
                  <span className="c-info-page-count">
                    {portalText("operations", "information.page_characters", {
                      v0: portalFormat(portalLocale()).number(page.charCount),
                    })}
                  </span>
                </button>
                {opened === page.ordinal ? (
                  <WebsitePageText voiceAgentId={voiceAgentId} agentVersionId={agentVersionId} ordinal={page.ordinal} />
                ) : null}
              </li>
            ))}
          </ul>
        </>
      )}

      {reading.skippedPage.length === 0 ? null : (
        <>
          <p className="c-va-caps">
            {portalText("operations", "information.pages_skipped", { v0: String(reading.pageSkippedCount) })}
          </p>
          <ul className="c-info-skipped">
            {reading.skippedPage.map((page, index) => (
              <li key={`${page.url}-${String(index)}`} className="c-info-skip">
                <span className="c-info-page-url">{page.url}</span>
                <span className="c-info-skip-reason">{skipReasonTextOf(page.reason)}</span>
              </li>
            ))}
          </ul>
          {reading.skippedPage.some((page) => page.reason === "content_type") ? (
            <p className="c-info-note">{portalText("operations", "information.skipped_files_note")}</p>
          ) : null}
        </>
      )}
    </div>
  );
}

function WebsitePageText({
  voiceAgentId,
  agentVersionId,
  ordinal,
}: {
  readonly voiceAgentId: string;
  readonly agentVersionId: string;
  readonly ordinal: number;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const page = useQuery(websitePageQuery(gateway, voiceAgentId, agentVersionId, ordinal));
  if (page.data === undefined) {
    return page.isError ? (
      <p className="c-note c-note-critical" role="alert">
        {portalText("operations", "information.page_unreadable")} {apiErrorText(page.error, "")}
      </p>
    ) : (
      <p className="c-note">{portalText("operations", "information.page_loading")}</p>
    );
  }
  return (
    <div className="c-info-page-text">
      <p className="c-info-note">{portalText("operations", "information.page_text_note")}</p>
      <pre className="c-info-page-pre">{page.data.text}</pre>
    </div>
  );
}

/** "v8 is being built from a fresh reading of your website. v7 keeps answering until you publish v8." */
export function RereadNotice({ made, liveLabel }: { readonly made: WebsiteRereadView; readonly liveLabel: string | null }): ReactNode {
  usePortalTranslation("operations");
  return (
    <p className="c-va-alert" role="status">
      <span className="c-va-alert-icon">
        <Icon glyph={GLYPH.checkCircle} size="lg" />
      </span>
      <span>
        {liveLabel === null
          ? portalText("operations", "information.read_again_started_no_live", { v0: made.agentVersionLabel })
          : portalText("operations", "information.read_again_started", { v0: made.agentVersionLabel, v1: liveLabel })}
      </span>
    </p>
  );
}
